import { RankingEntry } from '../../../shared/types/game.types';
import { fetchRankings } from './rankingService';

/**
 * 랭킹 관리 싱글톤 클래스
 */
class RankingManager {
  private static instance: RankingManager | null = null;

  private rankings: RankingEntry[] = [];
  private isLoading = false;
  private error: string | null = null;
  private listeners: Set<() => void> = new Set();
  private apiBaseUrl = '';

  // 페이지네이션 상태
  private currentPage = 1;
  private itemsPerPage = 10; // 페이지당 10개 (데이터 적을 때 보기 좋게)
  private totalItems = 0;
  private rankingsAsOf: string | null = null;
  private readonly TOP_LIMIT = 50;

  private constructor() {}

  static getInstance(): RankingManager {
    if (!RankingManager.instance) {
      RankingManager.instance = new RankingManager();
    }
    return RankingManager.instance;
  }

  /**
   * API Base URL 설정
   */
  setApiBaseUrl(url: string) {
    this.apiBaseUrl = url;
  }

  /**
   * 상태 변경 리스너 등록
   */
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * 모든 리스너에게 상태 변경 알림
   */
  private notify() {
    this.listeners.forEach(listener => listener());
  }

  /**
   * 현재 상태 반환
   */
  getState() {
    const cappedTotal = Math.min(this.totalItems, this.TOP_LIMIT);
    return {
      rankings: this.rankings,
      isLoading: this.isLoading,
      error: this.error,
      currentPage: this.currentPage,
      itemsPerPage: this.itemsPerPage,
      totalItems: cappedTotal,
      totalPages: Math.ceil(cappedTotal / this.itemsPerPage) || 1,
      apiBaseUrl: this.apiBaseUrl,
      rankingsAsOf: this.rankingsAsOf,
    };
  }

  /**
   * API Base URL 조회
   */
  getApiBaseUrl(): string {
    return this.apiBaseUrl;
  }

  /**
   * 랭킹 데이터 로드
   */
  async loadRankings(page = 1, limit = this.itemsPerPage, forceRefresh = false) {
    if (this.isLoading) return;

    if (!this.apiBaseUrl) {
      console.error('❌ API Base URL이 설정되지 않았습니다');
      return;
    }

    this.isLoading = true;
    this.error = null;
    // Top 50만 제공: page 범위를 미리 제한
    const maxPagesHard = Math.ceil(this.TOP_LIMIT / limit) || 1;
    this.currentPage = Math.min(Math.max(1, page), maxPagesHard);
    this.itemsPerPage = limit;
    this.notify();

    try {
      console.log(
        `🔄 랭킹 데이터 로드 시작 (page: ${page}, limit: ${limit}, force: ${forceRefresh})`
      );
      const { rankings, total, asOf } = await fetchRankings(
        this.apiBaseUrl,
        this.currentPage,
        limit,
        forceRefresh
      );
      this.rankings = rankings;
      this.totalItems = Math.min(total, this.TOP_LIMIT);
      this.rankingsAsOf = asOf || null;
      this.error = null;
      console.log(
        `✅ 랭킹 데이터 로드 완료 (count: ${rankings.length}, total: ${total})`
      );
      this.notify();
    } catch (error) {
      console.error('❌ 랭킹 정보를 불러오는데 실패했습니다:', error);
      this.error =
        error instanceof Error
          ? error.message
          : '랭킹 정보를 가져오는데 실패했습니다.';
      this.notify();
    } finally {
      this.isLoading = false;
      this.notify();
    }
  }

  /**
   * 랭킹 새로고침 (현재 페이지 유지)
   */
  async refreshRankings() {
    return this.loadRankings(this.currentPage, this.itemsPerPage, true);
  }

  /**
   * 페이지 변경
   */
  async changePage(page: number) {
    const maxPagesHard = Math.ceil(this.TOP_LIMIT / this.itemsPerPage) || 1;
    const next = Math.min(Math.max(1, page), maxPagesHard);
    return this.loadRankings(next, this.itemsPerPage);
  }

  /**
   * 캐시 무효화
   */
  invalidateCache() {
    console.log('🗑️ 랭킹 캐시 무효화');
    // rankingService 캐시 무효화 로직 필요 (현재는 서비스 내부 변수라 접근 불가, 추후 개선 필요)
  }
}

// 싱글톤 인스턴스 export
export const rankingManager = RankingManager.getInstance();
export default rankingManager;
